import { Component, Vue, Prop } from 'vue-property-decorator';
import { QuestionnaireDTO } from '@/models/QuestionnaireDTO';
import DoctorAutoComplete from '@/components/inputs/DoctorAutoComplete/DoctorAutoComplete.vue';
import LocalityAutoComplete from '@/components/inputs/LocalityAutoComplete/LocalityAutoComplete.vue';
import InputText from '@/components/inputs/InputText/InputText.vue';
import InputTel from '@/components/inputs/InputTel/InputTel.vue';
import InputCheckbox from '@/components/inputs/InputCheckbox/InputCheckbox.vue';
import { DoctorDTO } from '@/models/DoctorDTO';
import StepStatus from '@/components/StepStatus/StepStatus.vue';
import LogoutButton from '@/components/LogoutButton/LogoutButton.vue';

@Component({
  components: {
    DoctorAutoComplete,
    LocalityAutoComplete,
    InputText,
    InputTel,
    InputCheckbox,
    StepStatus,
    LogoutButton,
  },
})
export default class QuestDoctor extends Vue {
  @Prop({ required: false, default: false }) public isRecap!: boolean;
  public partId: number | undefined;
  public questionnaire: QuestionnaireDTO = {} as QuestionnaireDTO;
  public errorMessage: string = '';
  public error: boolean = false;
  public noDoctor: boolean = false;
  public noDoctorInList: boolean = false;
  public noDoctorOtherInList: boolean = false;
  public doctorDefault:DoctorDTO = {
    doctorId: -10,
    doctorName: this.$i18n.t('questionnaire.doctor.other'),
    doctorAddress: '',
    doctorAddressId: -10
  } as DoctorDTO
  public noDoctorDefault:DoctorDTO = {
    doctorId: -1,
    doctorName: this.$i18n.t('questionnaire.doctor.noDoctor'),
    doctorAddress: '',
    doctorAddressId: -1
  } as DoctorDTO

  /**
   * Get an array of all the property names from the questionnaire data in lowercase
   */
  get instanceFields(): string[] {
    if (this.$store.state.questionnaire.doctor) {
      let fields: string[] = [];
      Object.keys(this.questionnaire.doctor).forEach(e => fields.push(e.toLowerCase()));
      return fields;
    }
    return [];
  }
  
  /**
   * Get the errors returned by the API corresponding to the questionnaire data properties
   */
  get errors(): string[] {
    if (this.$store.state.questionnaireValidationErrors) {
      const obj = this.$store.state.questionnaireValidationErrors;
      let errorFields: string[] = [];
      Object.keys(obj).forEach((e) => {
        if (this.instanceFields.includes(e.toLowerCase())) {
          errorFields.push(obj[e].errors);
        }
      });
      return errorFields;
    }

    return [];
  }

  protected mounted() {
    this.noDoctorInList = this.$store.state.noDoctorInList;
    this.noDoctorOtherInList = this.$store.state.noDoctorOtherInList;
    this.partId = Number(this.$route.params.partId);
    // this.fetchQuestionnaire(this.partId);
    this.questionnaire = this.$store.state.questionnaire;

    // Set the value when doctor not in list
    if (this.questionnaire.doctor.doctor === null || 
      (this.questionnaire.doctor.doctor !== null && this.questionnaire.doctor.doctor.doctorId === -10)) {
      if (this.questionnaire.doctor.doctorLastname) {
        this.onNoDoctorInList(true);
      }
    }

    if (this.questionnaire.doctor.doctorOther === null ||
      (this.questionnaire.doctor.doctorOther !== null && this.questionnaire.doctor.doctorOther.doctorId === -10)) {
      if (this.questionnaire.doctor.doctorOtherLastname) {
        this.onNoDoctorOtherInList(true);
      }
    }

    // Restore noDoctor checkbox state (covers dossiers saved before the noDoctor checkbox existed)
    if (this.questionnaire.doctor.doctor === null && this.questionnaire.doctor.isValidated && !this.noDoctorInList) {
      this.noDoctor = true;
      this.questionnaire.doctor.doctor = this.noDoctorDefault;
    } else if (this.questionnaire.doctor.doctor?.doctorId === -1) {
      this.noDoctor = true;
    }

    if (this.questionnaire.doctor.isValidated && !this.noDoctorOtherInList) {
      if (this.questionnaire.doctor.doctorOther === null ||
        (this.questionnaire.doctor.doctorOther !== null && this.questionnaire.doctor.doctorOther.doctorId === -1)) {
          this.questionnaire.doctor.doctorOther = this.noDoctorDefault;
        }
    }
  }

  public onNoDoctor(checked: boolean) {
    this.noDoctor = checked;
    if (checked) {
      this.noDoctorInList = false;
      this.questionnaire.doctor.doctorLastname = null;
      this.questionnaire.doctor.doctorFirstname = null;
      this.questionnaire.doctor.doctorAddress = null;
      this.questionnaire.doctor.doctorLocality = null;
      this.questionnaire.doctor.doctorPhone = null;
      this.questionnaire.doctor.doctorOther = null;
    }
    this.questionnaire.doctor.doctor = checked ? this.noDoctorDefault : null;
  }

  /**
   * If checked, clears the doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoDoctorInList(checked: boolean) {
    this.noDoctorInList = checked;

    // reinitialize data about the doctor
    this.questionnaire.doctor.doctor = checked ? this.doctorDefault : null;
  }

  /**
   * If checked, clears the other doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoDoctorOtherInList(checked: boolean) {
    this.noDoctorOtherInList = checked;

    // reinitialize data about the doctor
    this.questionnaire.doctor.doctorOther = checked ? this.doctorDefault : null;
  }

  protected toDto(quest: QuestionnaireDTO): QuestionnaireDTO {
    let questClone = JSON.parse(JSON.stringify(quest));

    // A doctor was chosen in the dropdown, so clear manual data of the doctor
    if (!this.noDoctorInList && quest.doctor.doctor !== null) {
      questClone.doctor.doctorFirstname = null;
      questClone.doctor.doctorLastname = null;
      questClone.doctor.doctorAddress = null;
      questClone.doctor.doctorLocality = null;
      questClone.doctor.doctorPhone = null;

      // In case "No doctor" was chosen, clear the doctor object
      if (quest.doctor.doctor.doctorId < 0) {
        questClone.doctor.doctor = null;
      }
    }

    if (!this.noDoctorOtherInList && quest.doctor.doctorOther !== null) {
      questClone.doctor.doctorOtherFirstname = null;
      questClone.doctor.doctorOtherLastname = null;
      questClone.doctor.doctorOtherAddress = null;
      questClone.doctor.doctorOtherLocality = null;
      questClone.doctor.doctorOtherPhone = null;

      // In case "No doctor" was chosen, clear the doctor object
      if (quest.doctor.doctorOther.doctorId < 0) {
        questClone.doctor.doctorOther = null;
      }
    }

    // "Doctor not in list was checked" so clear the doctor object
    if (this.noDoctorInList) {
      questClone.doctor.doctor = null;
    }

    if (this.noDoctorOtherInList) {
      questClone.doctor.doctorOther = null;
    }

    return questClone;
  }

  protected async onSubmit(event: any) {
    this.$store.commit('setNoDoctorInList', this.noDoctorInList);
    this.$store.commit('setNoDoctorOtherInList', this.noDoctorOtherInList);
    this.$store.commit('setQuestionnaireDoctor', this.questionnaire.doctor);
    if (event.submitter.id === 'btn-continue') {
      this.$router.push({
        name: 'atHomeQuestions',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    } else {
      this.$router.push({
        name: 'atHomeInsurance',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    }
  }
}

